/* eslint max-lines: 0 */
import RadioButton from 'components/Buttons/RadioButton';
import DraggableCard from 'components/Draggable/DraggableCard';
import Dropdown from 'components/Input/dropdown/Dropdown';
import Modal from 'components/Modal';
import React, { useMemo, useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import gridColumnService from 'services/gridColumn.service';
import actions from 'state/actions';
import selectors from 'state/selectors';
import styled from 'styled-components/macro';
import IntlHelper from 'utils/helpers/intl.helper';
import tableHelper from 'utils/helpers/table.helper';
import { v4 as uuid } from 'uuid';

import DragArea from './DragArea';

const DragWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const RadioArea = styled.div`
  margin: 0 40px 20px;
`;

const SelectArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 40px 30px;
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.fontSizePalette.xxSmall};
  color: ${({ theme }) => theme.colorPalette.grey.grey9};
  font-weight: ${({ theme }) => theme.fontWeightPalette.light};
`;

const Columns = () => {
  const columns = useSelector(selectors.documents.selectTableColumns);
  const idUser = useSelector(selectors.user.getIdUser);
  const defaultFixedColumns = useSelector(selectors.documents.selectTableFixedColumns);
  const [fixedColumns, setFixedColumns] = useState(defaultFixedColumns);
  const defaultPageSize = useSelector(selectors.documents.selectTablePageSize);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const idTemplate = useSelector(selectors.documents.selectActiveTemplateId);
  const t = IntlHelper.useTranslator();
  const [visibleId, invisibleId] = useMemo(() => [uuid(), uuid()], []);
  const idProgram = useSelector(selectors.app.getProgramCode);

  const visible = [...columns].sort(tableHelper.sortColumnsByPosition).filter(({ position }) => position > -1);
  const invisible = columns.filter(({ position }) => position < 0);
  const data = {
    [visibleId]: {
      title: t('colonneModello'),
      items: visible,
    },
    [invisibleId]: {
      title: t('listainvisibili'),
      items: invisible,
    },
  };
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const secondDropdownRef = React.useRef<HTMLDivElement>(null);

  const [state, setState] = useState(data);

  const onDragEnd = (result: any, state: any, setState: any) => {
    if (!result.destination) return;
    const { source, destination } = result;

    if (source.droppableId !== destination.droppableId) {
      const sourceSection = state[source.droppableId];
      const destSection = state[destination.droppableId];
      const sourceItems = [...sourceSection.items];
      const destItems = [...destSection.items];
      const [removed] = sourceItems.splice(source.index, 1);
      destItems.splice(destination.index, 0, removed);
      setState({
        ...state,
        [source.droppableId]: {
          ...sourceSection,
          items: sourceItems,
        },
        [destination.droppableId]: {
          ...destSection,
          items: destItems,
        },
      });
    } else {
      const section = state[source.droppableId];
      const copiedItems = [...section.items];
      const [removed] = copiedItems.splice(source.index, 1);
      copiedItems.splice(destination.index, 0, removed);
      setState({
        ...state,
        [source.droppableId]: {
          ...section,
          items: copiedItems,
        },
      });
    }
  };

  const dispatch = useDispatch();

  const prepareData = () => {
    const visible = state[visibleId].items.map((item, i) => ({ ...item, position: i++ }));
    const invisible = state[invisibleId].items.map((item) => ({ ...item, position: -1 }));
    return {
      original: [...invisible, ...visible],
      prepared: [...invisible, ...visible].map(({ width, idTemplateColumn, position }) => ({
        width,
        idTemplateColumn,
        position,
      })),
    };
  };

  const moveAllTo = (sectionId: string) => {
    const visible = state[visibleId].items;
    const invisible = state[invisibleId].items;
    let newItems = [];
    if (sectionId === visibleId) {
      newItems = [...visible, ...invisible];
      setState({
        ...state,
        [visibleId]: {
          ...state[visibleId],
          items: newItems,
        },
        [invisibleId]: {
          ...state[invisibleId],
          items: [],
        },
      });
    } else {
      newItems = [...invisible, ...visible];
      setState({
        ...state,
        [visibleId]: {
          ...state[visibleId],
          items: [],
        },
        [invisibleId]: {
          ...state[invisibleId],
          items: newItems,
        },
      });
    }
  };

  const onRadioChange = (value: string) => {
    switch (value) {
      case 'addAll':
        moveAllTo(visibleId);
        break;
      case 'removeAll':
        moveAllTo(invisibleId);
    }
  };

  const onSelectChange = (option: any, info: any) => {
    const { value } = option;
    const { name } = info;
    switch (name) {
      case 'fixedColumns':
        setFixedColumns(value);
        break;
      case 'pagination':
        setPageSize(value);
    }
  };

  const onSave = async () => {
    const { prepared, original } = prepareData();
    if (idUser && idTemplate) {
      await gridColumnService.setDocumentGridColumns({
        idUser,
        idTemplate,
        fixedColumns,
        documentGridColumns: prepared,
      });
    }
    dispatch(actions.documents.setDocumentGridColumns(original));
    dispatch(actions.documents.setDocumentGridFixedColumns(fixedColumns));
    dispatch(actions.documents.setTablePageSize(pageSize));
    window.localStorage.setItem(`documents-${idProgram}-pageSize`, `${pageSize}`);
  };

  return (
    <>
      <Modal.Header noMargin title={t('ConfigTableColumns')} subtitle={t('lblInfoDrag')} />
      <Modal.Content>
        <DragWrapper>
          <DragDropContext onDragEnd={(result) => onDragEnd(result, state, setState)}>
            {Object.entries(state).map(([id, { title, items }]) => (
              <DragArea title={title} key={id} id={id}>
                {items.map((item, i) => (
                  <DraggableCard index={i} key={`${item.name}-${i}`} name={item.translate} />
                ))}
              </DragArea>
            ))}
          </DragDropContext>
        </DragWrapper>
        <RadioArea>
          <RadioButton
            inline
            onChange={onRadioChange}
            options={[
              { label: t('addAll'), value: 'addAll' },
              { label: t('removeAll'), value: 'removeAll' },
            ]}
          />
        </RadioArea>
        <SelectArea ref={dropdownRef}>
          <Label>{t('fixedColumns')}</Label>
          <Dropdown
            id="fixedColumns-dropdown"
            name="fixedColumns"
            onChange={onSelectChange}
            value={{ label: fixedColumns, value: fixedColumns }}
            width="200px"
            options={[
              {
                label: 0,
                value: 0,
              },
              {
                label: 1,
                value: 1,
              },
              {
                label: 2,
                value: 2,
              },
              {
                label: 3,
                value: 3,
              },
              {
                label: 4,
                value: 4,
              },
              {
                label: 5,
                value: 5,
              },
            ]}
            closeMenuOnScroll={(e) => {
              if (e.target instanceof Document) {
                const activeElement = e.target.activeElement;
                if (activeElement?.id.includes('react-select')) return false;
              }
              if (
                document.getElementById('react-select-portal')?.contains(e.target as Node) ||
                (e.target as HTMLElement)?.className?.toLowerCase()?.includes('menu')
              )
                return false;
              return true;
            }}
          />
        </SelectArea>
        <SelectArea ref={secondDropdownRef}>
          <Label>{t('pagination')}</Label>
          <Dropdown
            id="drop1"
            name="pagination"
            onChange={onSelectChange}
            value={{ label: pageSize, value: pageSize }}
            width="200px"
            options={[
              {
                label: 10,
                value: 10,
              },
              {
                label: 20,
                value: 20,
              },
              {
                label: 30,
                value: 30,
              },
              {
                label: 50,
                value: 50,
              },
              {
                label: 100,
                value: 100,
              },
              {
                label: 250,
                value: 250,
              },
            ]}
            closeMenuOnScroll={(e) => {
              if (e.target instanceof Document) {
                const activeElement = e.target.activeElement;
                if (activeElement?.id?.includes('react-select')) return false;
              }
              if (
                secondDropdownRef.current?.contains(e.target as Node) ||
                (e.target as HTMLElement)?.className?.toLowerCase()?.includes('menu') ||
                (e.target as HTMLElement)?.parentElement?.className?.toLowerCase()?.includes('menu')
              )
                return false;
              return true;
            }}
            isPortal={true}
          />
        </SelectArea>
      </Modal.Content>
      <Modal.Footer
        withCloseButton
        closeButtonText={t('close')}
        confirmAction={onSave}
        confirmText={t('salvaOptBtn')}
      />
    </>
  );
};

export default Columns;
